import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { farmvestService } from '../../../services/farmvest_api';
import { FarmvestFarm } from '../../../types/farmvest';
import { formatApiError } from '../../../services/apiUtils';

export const fetchFarms = createAsyncThunk(
    'farmvestFarms/fetchFarms',
    async (params: { location?: string; page?: number; size?: number; search?: string; name?: string; sort_by?: number } | string, { rejectWithValue }) => {
        try {
            // Normalize params
            const isString = typeof params === 'string';
            const location = isString ? params : params?.location;
            const page = isString ? 1 : params?.page || 1;
            const size = isString ? 15 : params?.size || 15;
            const search = isString ? '' : params?.search || '';
            const name = isString ? '' : params?.name || '';
            const sort_by = isString ? 2 : params?.sort_by || 2;

            const apiParams: any = {
                size,
                page,
                search,
                search_query: search, // Alias for robustness
                name,
                sort_by
            };

            // If location is provided and not 'ALL', use it as location_name filter if API supports it, 
            // otherwise the generic search might handle it.
            const hasFilters = (location && location.toUpperCase() !== 'ALL') || !!search || !!name;

            if (location && location.toUpperCase() !== 'ALL') {
                apiParams.location_name = location;
            }

            const response = await farmvestService.getAllFarms(apiParams);

            let allFarms: any[] = [];
            let totalCount = 0;
            let totalAnimalsCount = 0;
            let totalBuffaloesCount = 0;

            if (response) {
                const findArray = (obj: any): any[] => {
                    if (Array.isArray(obj)) return obj;
                    if (!obj || typeof obj !== 'object') return [];
                    const priorityKeys = ['farms', 'data', 'items', 'results', 'payload', 'records'];
                    for (const key of priorityKeys) {
                        if (Array.isArray(obj[key])) return obj[key];
                    }
                    return [];
                };

                allFarms = findArray(response);

                const findTotal = (obj: any): number => {
                    if (!obj || typeof obj !== 'object') return 0;
                    const pagination = obj.pagination || (obj.data && obj.data.pagination);
                    if (pagination && typeof pagination.total_items === 'number') return pagination.total_items;
                    if (pagination && typeof pagination.total_count === 'number') return pagination.total_count;

                    const countKeys = ['total_items', 'totalCount', 'total_count', 'total', 'count', 'total_farms'];
                    for (const key of countKeys) {
                        if (typeof obj[key] === 'number') return obj[key];
                    }
                    return allFarms.length;
                };

                totalCount = findTotal(response);

                // 1. Try to extract totals from primary response FIRST to avoid extra API hits
                const countFromPrimary = (
                    response.total_buffalo_count || response.total_buffaloes_count ||
                    response.data?.total_buffalo_count || response.data?.total_buffaloes_count || 0
                );

                const animalsFromPrimary = (
                    response.total_animals_count || response.total_animals ||
                    response.data?.total_animals_count || response.data?.total_animals || 0
                );

                // 2. Only fetch global stats if no filters are active AND primary response was missing the totals

                if (!hasFilters && (countFromPrimary === 0 || !countFromPrimary)) {
                    try {
                        const totalAnimalsResponse = await farmvestService.getTotalAnimals(undefined, undefined, 1, 1);
                        const globalTotal = totalAnimalsResponse?.pagination?.total_items ??
                            totalAnimalsResponse?.total_animals_count ??
                            totalAnimalsResponse?.total_animals ??
                            totalAnimalsResponse?.count ?? 0;

                        if (globalTotal > 0) {
                            totalAnimalsCount = globalTotal;
                            totalBuffaloesCount = totalAnimalsResponse?.total_buffalo_count ??
                                totalAnimalsResponse?.buffalo_count ??
                                totalAnimalsResponse?.buffaloes_count ??
                                totalAnimalsResponse?.total_buffaloes ??
                                totalAnimalsResponse?.total_buffalo ??
                                totalAnimalsResponse?.data?.total_buffalo_count ??
                                totalAnimalsResponse?.data?.buffalo_count ??
                                totalAnimalsResponse?.data?.buffaloes_count ?? 0;
                        } else {
                            totalAnimalsCount = animalsFromPrimary;
                            totalBuffaloesCount = countFromPrimary;
                        }
                    } catch (e) {
                        totalAnimalsCount = animalsFromPrimary;
                        totalBuffaloesCount = countFromPrimary;
                    }
                } else if (!hasFilters) {
                    // We aren't filtering, and we got counts from primary! Success.
                    totalAnimalsCount = animalsFromPrimary;
                    totalBuffaloesCount = countFromPrimary;
                } else {
                    // Use counts from current filtered response
                    totalAnimalsCount = animalsFromPrimary || response.data?.total_animals_count || response.data?.total_count || totalCount || 0;
                    totalBuffaloesCount = countFromPrimary || response.data?.total_buffalo_count || response.data?.total_buffaloes_count || 0;
                }
            }

            const mappedFarms: FarmvestFarm[] = allFarms.map((item: any, index: number) => {
                const farmName = item.farm_name || item.location_name || item.name || item.farmName || `Farm ${index + 1}`;
                const locRaw = item.location_name || item.location || item.locationName || item.city || null;
                let locationStr = typeof locRaw === 'string' ? locRaw : (locRaw?.name || locRaw?.location_name || null);

                const calculatedBuffaloes = item.sheds && Array.isArray(item.sheds)
                    ? item.sheds.reduce((s: number, shed: any) => s + (shed.current_buffaloes || shed.buffaloes_count || shed.buffalo_count || 0), 0)
                    : 0;

                const buffaloFromAPI = item.total_buffalo_count || item.total_buffaloes_count || 0;

                return {
                    id: item.id || item.farm_id || index,
                    farm_id: item.farm_id || item.id,
                    farm_name: farmName,
                    location: locationStr || '-',
                    address: item.address || '',
                    total_buffaloes_count: buffaloFromAPI || calculatedBuffaloes || 0,
                    local_quarantine_count: typeof item.local_quarantine_count === 'number'
                        ? item.local_quarantine_count
                        : (typeof item.local_quarantine === 'object' && item.local_quarantine !== null
                            ? item.local_quarantine.total_count ?? 0
                            : (typeof item.local_quarantine === 'number' ? item.local_quarantine : 0)),
                    total_calves_count: item.total_calves_count ?? item.total_calves ?? 0,
                    ready_for_delivery_count: item.ready_for_delivery_count ?? 0,
                    local_quarantine: typeof item.local_quarantine === 'object' ? item.local_quarantine : null,
                    farm_manager_name: item.farm_manager_name || item.manager_name || (item.farm_manager?.name) || '-',
                    mobile_number: item.mobile_number || item.manager_mobile || (item.farm_manager?.mobile) || '-',
                    sheds_count: item.sheds_count || item.shed_count || (item.sheds?.length) || 0,
                    calves_shed_capacity: item.calves_shed_capacity,
                    calves_sheds: item.calves_sheds,
                    is_test: item.is_test,
                    latitude: item.latitude,
                    location_name: item.location_name,
                    longitude: item.longitude,
                    quarantine_rows_per_shed: item.quarantine_rows_per_shed,
                    quarantine_shed_capacity: item.quarantine_shed_capacity,
                    quarantine_sheds: item.quarantine_sheds,
                    quarantine_slots_per_row: item.quarantine_slots_per_row,
                    rows_per_shed: item.rows_per_shed,
                    shed_capacity: item.shed_capacity,
                    shed_count: item.shed_count,
                    slots_per_row: item.slots_per_row,
                    farm_manager: item.farm_manager,
                    admin: item.admin
                };
            });

            // Ensure the global total reflects what's in the table if it's currently zero or mismatched
            const summedBuffaloes = mappedFarms.reduce((sum: number, farm: any) => sum + Number(farm.total_buffaloes_count || 0), 0);

            if (totalBuffaloesCount === 0 || !totalBuffaloesCount) {
                totalBuffaloesCount = summedBuffaloes;
            }

            if (totalAnimalsCount === 0 || !totalAnimalsCount) {
                totalAnimalsCount = totalBuffaloesCount || summedBuffaloes;
            }

            return {
                farms: mappedFarms,
                totalCount: totalCount || mappedFarms.length,
                totalAnimalsCount,
                totalBuffaloesCount,
                location: location || 'ALL',
                page: page,
                isGlobal: !hasFilters
            };
        } catch (error: any) {
            return rejectWithValue(formatApiError(error.response?.data?.detail || error.message || 'Failed to fetch farms'));
        }
    }
);

interface FarmsState {
    farms: FarmvestFarm[];
    loading: boolean;
    error: string | null;
    loadedLocation: string | null;
    loadedPage: number;
    totalCount: number;
    totalAnimalsCount: number;
    totalBuffaloesCount: number;
    overallTotalCount: number;
    overallTotalBuffaloesCount: number;
}

const initialState: FarmsState = {
    farms: [],
    loading: false,
    error: null,
    loadedLocation: null,
    loadedPage: 1,
    totalCount: 0,
    totalAnimalsCount: 0,
    totalBuffaloesCount: 0,
    overallTotalCount: 0,
    overallTotalBuffaloesCount: 0
};

const farmsSlice = createSlice({
    name: 'farmvestFarms',
    initialState,
    reducers: {
        setFarms: (state, action: PayloadAction<FarmvestFarm[]>) => {
            state.farms = Array.isArray(action.payload) ? action.payload : [];
        },
        clearFarmsError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFarms.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFarms.fulfilled, (state, action) => {
                state.loading = false;
                state.farms = action.payload.farms;
                state.totalCount = action.payload.totalCount;
                state.totalAnimalsCount = action.payload.totalAnimalsCount || 0;
                state.totalBuffaloesCount = action.payload.totalBuffaloesCount || 0;
                state.loadedLocation = action.payload.location;
                state.loadedPage = action.payload.page;

                // Persist global counts for stat cards if this was a global fetch or if they aren't set yet
                if (action.payload.isGlobal || state.overallTotalCount === 0) {
                    state.overallTotalCount = action.payload.totalCount;
                }
                if (action.payload.isGlobal || state.overallTotalBuffaloesCount === 0) {
                    state.overallTotalBuffaloesCount = action.payload.totalBuffaloesCount || 0;
                }
            })
            .addCase(fetchFarms.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.farms = [];
                state.totalCount = 0;
            });
    },
});

export const { setFarms, clearFarmsError } = farmsSlice.actions;
export const farmsReducer = farmsSlice.reducer;
export default farmsSlice.reducer;
